import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Star, Check, Zap, Clock, Users, BarChart3, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const FeaturedJobUpgrade = () => {
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(params.get('job_id') || '');
  const [urgent, setUrgent] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [starting, setStarting] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | verifying | success | expired
  const sessionId = params.get('session_id');

  useEffect(() => {
    document.title = 'Featured Social Work Job | INASW';
    axios.get(`${API_URL}/featured-job/config`).then((r) => setCfg(r.data)).catch(() => setCfg(null));
    axios.get(`${API_URL}/jobs/my-jobs`, { headers: authHeaders() })
      .then((r) => setJobs((r.data || []).filter((j) => !j.is_external && j.status === 'active')))
      .catch(() => setJobs([]));
  }, []);

  const pollStatus = useCallback(async (sid, attempt = 0) => {
    if (attempt > 8) { setPhase('idle'); toast.error('Payment status check timed out. Refresh to see your featured job.'); return; }
    try {
      const r = await axios.get(`${API_URL}/jobs/feature/checkout/status/${sid}`, { headers: authHeaders() });
      if (r.data.status === 'complete') { setPhase('success'); toast.success('Your job is now Featured!'); return; }
      if (r.data.status === 'expired') { setPhase('expired'); return; }
      setTimeout(() => pollStatus(sid, attempt + 1), 1800);
    } catch (e) { setTimeout(() => pollStatus(sid, attempt + 1), 1800); }
  }, []);

  useEffect(() => {
    if (sessionId) { setPhase('verifying'); pollStatus(sessionId); }
  }, [sessionId, pollStatus]);

  const startCheckout = async () => {
    if (!jobId) { toast.error('Please choose which job to feature.'); return; }
    setStarting(true);
    try {
      const r = await axios.post(`${API_URL}/jobs/${jobId}/feature/checkout`,
        { origin_url: window.location.origin, urgently_hiring: urgent, featured_start: startDate || '' }, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setStarting(false);
    }
  };

  if (phase === 'verifying') {
    return <div className="min-h-screen bg-[#F7F5EF] pt-32 text-center" data-testid="featured-job-verifying">
      <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /><p className="mt-3 text-gray-600">Confirming your payment…</p></div>;
  }
  if (phase === 'success') {
    return <div className="min-h-screen bg-[#F7F5EF] pt-32 px-4 text-center" data-testid="featured-job-success">
      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
      <h1 className="font-serif text-2xl font-bold text-primary mt-3">Your job is now Featured!</h1>
      <p className="text-gray-600 mt-2 max-w-md mx-auto">It now appears above imported listings, carries the Featured badge and your logo, and can invite qualified candidates.</p>
      <Link to="/employer/dashboard"><Button className="mt-5 bg-primary text-white">Back to dashboard</Button></Link>
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16" data-testid="featured-job-upgrade">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/employer/dashboard" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> Back to dashboard</Link>

        <div className="bg-white rounded-2xl border-2 border-secondary/30 shadow-sm overflow-hidden">
          <div className="bg-primary text-white p-6 sm:p-8">
            <div className="flex items-center gap-2 text-secondary font-bold uppercase tracking-widest text-xs"><Star className="w-4 h-4 fill-current" /> Employer Upgrade</div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mt-2">Featured Social Work Job</h1>
            <p className="text-white/85 mt-2 text-base font-semibold">{cfg?.message || 'No pay-per-click charges. Know exactly what your recruitment will cost.'}</p>
            {cfg?.coming_soon && <span className="inline-block mt-4 bg-secondary text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase" data-testid="featured-job-coming-soon">Coming Soon</span>}
          </div>

          <div className="p-6 sm:p-8">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl bg-primary/5 p-4 text-center"><p className="text-3xl font-bold text-primary" data-testid="featured-job-price">{cfg ? `$${cfg.price}` : '—'}</p><p className="text-xs text-gray-500 mt-1">One flat price</p></div>
              <div className="rounded-xl bg-primary/5 p-4 text-center"><p className="text-3xl font-bold text-primary flex items-center justify-center gap-1"><Clock className="w-5 h-5" />{cfg?.duration_days ?? '—'}</p><p className="text-xs text-gray-500 mt-1">Days featured</p></div>
              <div className="rounded-xl bg-primary/5 p-4 text-center"><p className="text-3xl font-bold text-primary flex items-center justify-center gap-1"><Users className="w-5 h-5" />{cfg?.invite_allowance ?? '—'}</p><p className="text-xs text-gray-500 mt-1">Candidate invitations</p></div>
            </div>

            <h2 className="font-serif text-xl font-bold text-primary mb-3">What you get</h2>
            <ul className="space-y-2 mb-6">
              {(cfg?.benefits || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-testid={`featured-benefit-${i}`}><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {b}</li>
              ))}
            </ul>

            <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-4 text-sm text-gray-600 mb-6">
              <p className="flex items-center gap-2 font-semibold text-primary"><Zap className="w-4 h-4 text-red-600" /> Only for verified employers &amp; directly-posted jobs</p>
              <p className="mt-1">Imported/aggregated listings aren't eligible. Payment doesn't bypass job verification, salary requirements, expiration rules, or content standards. Priority placement is removed automatically when the featured period ends.</p>
            </div>

            {cfg?.coming_soon ? (
              <Button disabled className="w-full h-12 bg-gray-300 text-gray-600 cursor-not-allowed" data-testid="featured-job-cta-disabled">Payment Coming Soon — check back shortly</Button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Which job do you want to feature?</label>
                  <select value={jobId} onChange={(e) => { setJobId(e.target.value); const n = new URLSearchParams(params); e.target.value ? n.set('job_id', e.target.value) : n.delete('job_id'); setParams(n, { replace: true }); }}
                    className="mt-1 w-full h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm" data-testid="featured-job-select">
                    <option value="">— Select one of your active jobs —</option>
                    {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}{j.location ? ` · ${j.location}` : ''}</option>)}
                  </select>
                  {jobs.length === 0 && <p className="text-xs text-gray-500 mt-1">You have no eligible (active, directly-posted) jobs yet. <Link to="/employer/post-job" className="text-primary underline">Post a job</Link> first.</p>}
                </div>
                {cfg?.urgently_hiring_enabled && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} className="w-4 h-4" data-testid="featured-job-urgent" />
                    Add an <strong>Urgently Hiring</strong> label (means you're actively reviewing applicants)
                  </label>
                )}
                <div>
                  <label className="text-sm font-semibold text-gray-700">Schedule a start date <span className="font-normal text-gray-500">(optional — leave blank to start immediately)</span></label>
                  <input type="date" value={startDate} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                    onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm" data-testid="featured-job-start-date" />
                </div>
                <Button onClick={startCheckout} disabled={starting || !jobId} className="w-full h-12 bg-secondary hover:bg-secondary-hover text-white" data-testid="featured-job-cta">
                  {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Upgrade this job for ${cfg?.price}
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-400 text-center mt-3">You'll see exactly what you get and how many invitations are included before any payment.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3 text-center text-gray-500">
          <div className="flex flex-col items-center gap-1"><BarChart3 className="w-5 h-5 text-primary" /><span className="text-xs">Full performance analytics</span></div>
          <div className="flex flex-col items-center gap-1"><Star className="w-5 h-5 text-secondary" /><span className="text-xs">Above imported jobs</span></div>
          <div className="flex flex-col items-center gap-1"><Users className="w-5 h-5 text-primary" /><span className="text-xs">Private candidate invites</span></div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedJobUpgrade;
